import * as React from 'react'

type TFunc = (key: string) => string
type RenderDiff = (before: unknown, after: unknown) => { before: React.ReactNode; after: React.ReactNode }

const EXCLUDED_CONFIG_FIELDS = new Set(['currencies', 'configurationId'])

function formatConfigValue(field: string, val: unknown, t: TFunc): string {
  if (field === 'calculationStartDate') {
    return val ? String(val).slice(0, 10) : '—'
  }
  if (field === 'accommodationFilterOptions') {
    if (val === 0) return t('configuration.minimumWages.accommodationFilterOptionAll')
    if (val === 1) return t('configuration.minimumWages.accommodationFilterOptionWithoutLongRests')
    return String(val)
  }
  if (typeof val === 'boolean') return val ? 'Tak' : 'Nie'
  if (val === null || val === undefined) return '—'
  return String(val)
}

function makeConfigRenderer(translateKey: string, t: TFunc): RenderDiff {
  return (before, after) => {
    const b = (before as Record<string, unknown>) ?? {}
    const a = (after as Record<string, unknown>) ?? {}
    const changedKeys = [...new Set([...Object.keys(b), ...Object.keys(a)])]
      .filter(k => !EXCLUDED_CONFIG_FIELDS.has(k))
      .filter(k => JSON.stringify(b[k]) !== JSON.stringify(a[k]))

    if (!changedKeys.length) return { before: <span>—</span>, after: <span>—</span> }

    const getLabel = (field: string): string => {
      if (translateKey) {
        const countryKey = `configuration.minimumWages.${translateKey}.${field}`
        const countryLabel = t(countryKey)
        if (countryLabel && countryLabel !== countryKey && !countryLabel.startsWith('Missing translationId')) return countryLabel
      }
      const genericKey = `configuration.minimumWages.${field}`
      const genericLabel = t(genericKey)
      if (genericLabel && genericLabel !== genericKey && !genericLabel.startsWith('Missing translationId')) return genericLabel
      return field
    }

    return {
      before: (
        <div className="tw-flex tw-flex-col tw-gap-1">
          {changedKeys.map(k => (
            <div key={k} className="tw-text-xs">
              <span className="tw-font-medium">{getLabel(k)}: </span>
              <span className="tw-line-through">{formatConfigValue(k, b[k], t)}</span>
            </div>
          ))}
        </div>
      ),
      after: (
        <div className="tw-flex tw-flex-col tw-gap-1">
          {changedKeys.map(k => (
            <div key={k} className="tw-text-xs">
              <span className="tw-font-medium">{getLabel(k)}: </span>
              <span>{formatConfigValue(k, a[k], t)}</span>
            </div>
          ))}
        </div>
      ),
    }
  }
}

function makeArrayRenderer(): RenderDiff {
  return (before, after) => {
    const b = Array.isArray(before) ? before : []
    const a = Array.isArray(after) ? after : []
    const getId = (item: Record<string, unknown>) => item.configurationValueId as number | undefined

    const added = a.filter((item: Record<string, unknown>) => !getId(item) || !b.some((bi: Record<string, unknown>) => getId(bi) === getId(item)))
    const removed = b.filter((item: Record<string, unknown>) => getId(item) && !a.some((ai: Record<string, unknown>) => getId(ai) === getId(item)))
    const changed = a.filter((item: Record<string, unknown>) => {
      if (!getId(item)) return false
      const bItem = b.find((bi: Record<string, unknown>) => getId(bi) === getId(item))
      return bItem && JSON.stringify(bItem) !== JSON.stringify(item)
    })

    if (!added.length && !removed.length && !changed.length) {
      return { before: <span>—</span>, after: <span>—</span> }
    }

    const formatItem = (item: Record<string, unknown>): string => {
      const rawDate = item.dateFrom ?? item.rateDate
      const date = rawDate ? String(rawDate).slice(0, 10) : ''
      const value = item.rateValue ?? item.allowanceValue ?? item.accomodationValue ?? item.additionValue
      const currencyObj = item.rateCurrency ?? item.allowanceCurrency ?? item.accomodationCurrency ?? item.additionCurrency
      const currency = (currencyObj as Record<string, unknown>)?.code as string | undefined
      const parts: string[] = [date]
      if (value != null && currency) parts.push(`${value} ${currency}`)
      else if (value != null) parts.push(String(value))
      return parts.filter(Boolean).join(', ')
    }

    return {
      before: (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs">
          {removed.map((item, i) => (
            <div key={i} className="tw-line-through">{formatItem(item as Record<string, unknown>)}</div>
          ))}
          {changed.map((item) => {
            const bItem = b.find((bi: Record<string, unknown>) => getId(bi) === getId(item as Record<string, unknown>))
            return <div key={String(getId(item as Record<string, unknown>))} className="tw-line-through">{formatItem(bItem as Record<string, unknown>)}</div>
          })}
          {!removed.length && !changed.length && <span>—</span>}
        </div>
      ),
      after: (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-text-xs">
          {added.map((item, i) => <div key={i}>{formatItem(item as Record<string, unknown>)}</div>)}
          {changed.map((item) => <div key={String(getId(item as Record<string, unknown>))}>{formatItem(item as Record<string, unknown>)}</div>)}
          {!added.length && !changed.length && <span>—</span>}
        </div>
      ),
    }
  }
}

export function buildMinimumWageDiffRenderers(
  translateKey: string,
  t: TFunc,
): Record<string, RenderDiff> {
  const arrayRenderer = makeArrayRenderer()
  return {
    configuration: makeConfigRenderer(translateKey, t),
    expandableItems: arrayRenderer,
    allowanceExpandableItems: arrayRenderer,
    accomodationExpandableItems: arrayRenderer,
    countryAdditionsExpandableItems: arrayRenderer,
    hourRateExpandableItems: arrayRenderer,
  }
}
